import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, map, catchError } from 'rxjs/operators';

import * as Enumerable from 'linq';

import { ISearcher } from '../interfaces/search.interface';
import { SecurityRoleType } from '@jms/model';

export interface EntitySearchParams {
  entityId: string;
  query: string;
}

export interface UserSearcherParams {
  security: SecurityRoleType[];
  query: string;
  parentId?: string;
}

export class CoreDataSearcher<T> implements ISearcher {
  protected _searchSubject = new Subject<string>();
  public readonly data$ = new BehaviorSubject<T[]>([]);

  constructor(
    protected http: HttpClient,
    protected endPoint: string,
    protected allowReturnAll = true,
    protected existing: any,
    protected customFilter?: any
  ) {
    this._searchSubject
      .pipe(
        debounceTime(300),
        filter(query => {
          if (this.allowReturnAll) return true;
          return query ? true : false;
        })
      )
      .subscribe(query => {
        this.getData(query);
      });
  }

  public search(query?: string) {
    this._searchSubject.next(`filter=${query}`);
  }

  public searchByParams(params: any) {
    this._searchSubject.next(params ? this.toQueryString(params) : '');
  }

  public getResult() {
    return this.data$;
  }

  public getData(query: string, existing: () => T[] = () => []) {

    const hasQuery = this.endPoint.split('?').length > 1;
    const querySymbol = hasQuery ? '&' : '?';

    const url = this.customFilter
      ? this.formatUrl(query)
      : query ? `${this.endPoint}${querySymbol}${query}` : this.endPoint;


    this.http
      .get<T[]>(url)
      .pipe(
        map(items => {

          const existingData = existing();

          const data = existingData?.length
            ? Enumerable.from(items)
              .except(existing(), c => ((<any>c).name ? (<any>c).name : (<any>c)._id))
              .toArray() as T[]
            : items;

          this.data$.next(data ? data : []);
        }),
        catchError(x => {
          return null;
        })
      )
      .subscribe();
  }

  private formatUrl(query: string): string {

    let url = `${this.endPoint}`;
    const keys = Object.keys(this.customFilter);

    Enumerable
      .from(keys)
      .forEach((key, i) => {

        if (i === 0) {
          url += '?';
        } else {
          url += '&&';
        }

        url += `${key}=${this.customFilter[key]}`;
      });

    url = query ? `${url}&&${query}` : url;

    return url;

  }

  private toQueryString(paramsObject) {
    return Object.keys(paramsObject)
      .filter(key => paramsObject[key])
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(paramsObject[key])}`)
      .join('&');
  }
}
